import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { AgGridReact } from "ag-grid-react";
import type { CellClassParams, CellValueChangedEvent } from "ag-grid-community";
import { apiPatch } from "@/services/genesisApi/httpsMethods";
import type { ApiResponse } from "@/services/genesisApi/httpsMethods";
import { isRecordFieldEqual, rowIdKey } from "@/utils/compareData";
import type { Deal, KillReason } from "@/types/deal";
import { hasKillReason, isKilledStatus } from "@/utils/dealStatusHelpers";
import { getStatusName } from "@/utils/dealValidation";
import {
  advisorFeeReminderForStatusChange,
  buildCellPatch,
  conflictFromMessage,
  isAccordionRow,
  missingFieldsForStatusChange,
  type AdvisorFeeReminder,
  type TermSheetDialogState,
} from "./pipelineCellSave";

const CONFLICT_HIGHLIGHT_MS = 6_000;

/** A move to Killed waiting for the user to pick a kill reason. */
export type PipelineKillRequest = {
  row: Deal;
  dealName: string;
  status: unknown;
};

export type PipelineConflictNotice = {
  rowId: string;
  field: string;
  fieldLabel: string;
  dealName: string;
  mine: unknown;
  theirs: unknown;
  changedBy: string | null;
  changedAt: string | null;
};

type SetRows = (value: Deal[] | ((prev: Deal[]) => Deal[])) => void;

type UsePipelineCellSaveArgs = {
  gridRef: RefObject<AgGridReact<Deal> | null>;
  /** Last server snapshot; the grid mutates row objects, so old values come from here. */
  initialDeals: Deal[];
  setDeals: SetRows;
  setInitialDeals: SetRows;
  showErrorFromResponse: (response: ApiResponse) => void;
  /** Called once a save succeeds, so the live refresh does not flash our own edit. */
  onSaved: (rowId: string) => void;
  /** Re-sync the grid with the server (used after a conflict). */
  refresh: () => void;
  /** Tenant tracks advisor fees: remind about quotes when a row moves into closing. */
  advisorFeesEnabled?: boolean;
};

const cellKey = (rowId: string, field: string) => `${rowId}::${field}`;

function patchRow(
  rows: Deal[],
  rowId: string,
  fields: Record<string, unknown>,
) {
  return rows.map((row) =>
    rowIdKey(row.id) === rowId ? ({ ...row, ...fields } as Deal) : row,
  );
}

/**
 * Saves each Pipeline cell as soon as it is edited (PATCH of one field).
 *
 * - Optimistic: the grid already shows the new value; the cell is marked as
 *   saving until the API answers, and saves of the same cell are queued.
 * - 409 (someone else changed the field since this tab loaded it): the cell
 *   goes back to the stored value, is highlighted, and a notice offers to
 *   apply this user's value anyway.
 * - Other failures put the old value back and show the error.
 * - A row moving into an advanced status without the required fields is put
 *   back and the existing term-sheet dialog lists what is missing.
 * - A row saved into closing without advisor fee quotes gets a reminder
 *   (`feeReminder`); the save itself is never blocked.
 * - A deal moved to Killed without a kill reason is put back until the user
 *   picks one (`killRequest`); status and reason are then saved together.
 */
export function usePipelineCellSave({
  gridRef,
  initialDeals,
  setDeals,
  setInitialDeals,
  showErrorFromResponse,
  onSaved,
  refresh,
  advisorFeesEnabled = false,
}: UsePipelineCellSaveArgs) {
  const initialByIdRef = useRef(new Map<string, Deal>());
  useEffect(() => {
    initialByIdRef.current = new Map(
      initialDeals.map((row) => [rowIdKey(row.id) ?? "", row]),
    );
  }, [initialDeals]);
  // Value the server confirmed for a cell whose later saves are still queued
  // (the snapshot state may not have caught up yet).
  const confirmedRef = useRef(new Map<string, unknown>());
  const savingCellsRef = useRef(new Set<string>());
  const conflictCellsRef = useRef(new Set<string>());
  const queuesRef = useRef(new Map<string, Promise<void>>());
  const [savingCount, setSavingCount] = useState(0);
  const [conflict, setConflict] = useState<PipelineConflictNotice | null>(null);
  const [termSheetDialog, setTermSheetDialog] = useState<TermSheetDialogState>({
    open: false,
    rows: [],
  });
  const [feeReminder, setFeeReminder] = useState<AdvisorFeeReminder | null>(
    null,
  );
  const [killRequest, setKillRequest] = useState<PipelineKillRequest | null>(
    null,
  );

  const refreshCell = useCallback(
    (rowId: string, field: string) => {
      const api = gridRef.current?.api;
      if (!api || api.isDestroyed()) return;
      const rowNode = api.getRowNode(rowId);
      if (!rowNode) return;
      api.refreshCells({ rowNodes: [rowNode], columns: [field], force: true });
    },
    [gridRef],
  );

  /** Stored (API-shaped) value of a cell as the server last reported it. */
  const storedValue = useCallback((rowId: string, field: string) => {
    const key = cellKey(rowId, field);
    if (confirmedRef.current.has(key)) return confirmedRef.current.get(key);
    const row = initialByIdRef.current.get(rowId) as
      Record<string, unknown> | undefined;
    return row?.[field];
  }, []);

  /**
   * Put a stored value on the row. Written directly (not via setDataValue)
   * because some columns' value setters only accept dropdown options.
   */
  const writeCell = useCallback(
    (rowId: string, field: string, value: unknown) => {
      const rowNode = gridRef.current?.api?.getRowNode(rowId);
      if (rowNode?.data) {
        (rowNode.data as unknown as Record<string, unknown>)[field] = value;
      }
      setDeals((rows) => patchRow(rows, rowId, { [field]: value }));
      refreshCell(rowId, field);
    },
    [gridRef, refreshCell, setDeals],
  );

  const save = useCallback(
    async (
      row: Deal,
      field: string,
      fieldLabel: string,
      newValue: unknown,
      extraChanges: Record<string, unknown> = {},
    ) => {
      const rowId = rowIdKey(row.id);
      if (!rowId) return;
      // Read when the save starts, so a queued save expects the value the
      // previous save of this cell left behind.
      const oldValue = storedValue(rowId, field);
      const patch = buildCellPatch(
        row,
        field,
        newValue,
        oldValue,
        extraChanges,
      );
      if (!patch) return;

      const key = cellKey(rowId, field);
      savingCellsRef.current.add(key);
      conflictCellsRef.current.delete(key);
      setSavingCount((n) => n + 1);
      refreshCell(rowId, field);

      try {
        const response = await apiPatch<{ deal?: Deal | null }>(
          patch.endpoint,
          patch.body,
        );

        if (response.success) {
          confirmedRef.current.set(key, newValue);
          const savedRow = response.data?.deal ?? null;
          // The saved row says which fee quotes the new status still needs.
          const extra =
            savedRow && "advisor_fees_missing" in savedRow
              ? { advisor_fees_missing: savedRow.advisor_fees_missing }
              : {};
          setInitialDeals((rows) =>
            patchRow(rows, rowId, {
              [field]: newValue,
              ...extraChanges,
              ...extra,
            }),
          );
          if ("advisor_fees_missing" in extra) {
            setDeals((rows) => patchRow(rows, rowId, extra));
          }
          if (field === "status" && advisorFeesEnabled) {
            const reminder = advisorFeeReminderForStatusChange(
              row,
              newValue,
              oldValue,
              savedRow,
            );
            if (reminder) setFeeReminder(reminder);
          }
          onSaved(rowId);
          return;
        }

        writeCell(rowId, field, oldValue);
        for (const extraField of Object.keys(extraChanges)) {
          writeCell(rowId, extraField, storedValue(rowId, extraField));
        }

        if (response.status === 409) {
          const detail = conflictFromMessage(response.message, patch.key);
          conflictCellsRef.current.add(key);
          setTimeout(() => {
            conflictCellsRef.current.delete(key);
            refreshCell(rowId, field);
          }, CONFLICT_HIGHLIGHT_MS);
          setConflict({
            rowId,
            field,
            fieldLabel,
            dealName: row.deal_name || "this deal",
            mine: newValue,
            theirs: detail?.current ?? null,
            changedBy: detail?.changed_by ?? null,
            changedAt: detail?.changed_at ?? null,
          });
          // Pull in the other person's value (it flashes like any live change).
          refresh();
          return;
        }

        showErrorFromResponse(response);
      } finally {
        savingCellsRef.current.delete(key);
        setSavingCount((n) => n - 1);
        refreshCell(rowId, field);
      }
    },
    [
      advisorFeesEnabled,
      onSaved,
      refresh,
      refreshCell,
      setDeals,
      setInitialDeals,
      showErrorFromResponse,
      storedValue,
      writeCell,
    ],
  );

  /** Queue the save behind any in-flight save of the same cell. */
  const enqueue = useCallback(
    (
      row: Deal,
      field: string,
      fieldLabel: string,
      newValue: unknown,
      extraChanges?: Record<string, unknown>,
    ) => {
      const rowId = rowIdKey(row.id);
      if (!rowId) return Promise.resolve();
      const key = cellKey(rowId, field);
      const previous = queuesRef.current.get(key) ?? Promise.resolve();
      const next = previous
        .catch(() => undefined)
        .then(() => save(row, field, fieldLabel, newValue, extraChanges));
      queuesRef.current.set(key, next);
      void next.finally(() => {
        if (queuesRef.current.get(key) === next) {
          // Nothing else queued: the snapshot is the source of truth again.
          queuesRef.current.delete(key);
          confirmedRef.current.delete(key);
        }
      });
      return next;
    },
    [save],
  );

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<Deal>) => {
      const { colDef, data } = event;
      const field = colDef.field;
      if (!data || !field) return;
      const rowId = rowIdKey(data.id);
      if (!rowId) return;

      // The column's valueSetter has written the stored (API) shape onto the
      // row; event.oldValue is the valueGetter's display shape, so compare
      // against the last stored value instead.
      const committed = (data as unknown as Record<string, unknown>)[field];
      const previous = storedValue(rowId, field);
      if (isRecordFieldEqual(committed, previous)) return;

      if (field === "status") {
        const missingFields = missingFieldsForStatusChange(
          data,
          committed,
          previous,
        );
        if (missingFields.length > 0) {
          writeCell(rowId, field, previous);
          setTermSheetDialog({
            open: true,
            rows: [
              {
                id: data.id ?? 0,
                dealName: data.deal_name || "Unnamed deal",
                missingFields,
              },
            ],
          });
          return;
        }
        if (
          isKilledStatus(getStatusName(committed)) &&
          !isAccordionRow(data) &&
          !hasKillReason(data)
        ) {
          writeCell(rowId, field, previous);
          setKillRequest({
            row: data,
            dealName: data.deal_name || "Unnamed deal",
            status: committed,
          });
          return;
        }
      }

      setDeals((rows) => patchRow(rows, rowId, { [field]: committed }));
      void enqueue(data, field, colDef.headerName ?? field, committed);
    },
    [enqueue, setDeals, storedValue, writeCell],
  );

  const applyMine = useCallback(() => {
    if (!conflict) return;
    const row = gridRef.current?.api?.getRowNode(conflict.rowId)?.data;
    setConflict(null);
    if (!row) return;
    // The expected value is now the other person's (the refreshed snapshot).
    writeCell(conflict.rowId, conflict.field, conflict.mine);
    void enqueue(row, conflict.field, conflict.fieldLabel, conflict.mine);
  }, [conflict, enqueue, gridRef, writeCell]);

  const dismissConflict = useCallback(() => setConflict(null), []);
  const closeTermSheetDialog = useCallback(
    () => setTermSheetDialog({ open: false, rows: [] }),
    [],
  );
  const dismissFeeReminder = useCallback(() => setFeeReminder(null), []);
  const cancelKill = useCallback(() => setKillRequest(null), []);
  const confirmKill = useCallback(
    (reason: KillReason | null) => {
      if (!killRequest) return;
      const { row, status } = killRequest;
      setKillRequest(null);
      const rowId = rowIdKey(row.id);
      if (!rowId) return;
      writeCell(rowId, "status", status);
      if (!reason) {
        // Killed without a reason: the data check flags it for the lead.
        setDeals((rows) => patchRow(rows, rowId, { status }));
        void enqueue(row, "status", "Status", status);
        return;
      }
      writeCell(rowId, "kill_reason", reason);
      setDeals((rows) =>
        patchRow(rows, rowId, { status, kill_reason: reason }),
      );
      void enqueue(row, "status", "Status", status, { kill_reason: reason });
    },
    [enqueue, killRequest, setDeals, writeCell],
  );

  // Stable object: the rules read the refs when AG Grid evaluates them.
  const cellClassRules = useMemo(
    () => ({
      "pipeline-cell--saving": (params: CellClassParams<Deal>) => {
        const rowId = rowIdKey(params.data?.id);
        const field = params.colDef.field;
        return (
          !!rowId &&
          !!field &&
          savingCellsRef.current.has(cellKey(rowId, field))
        );
      },
      "pipeline-cell--conflict": (params: CellClassParams<Deal>) => {
        const rowId = rowIdKey(params.data?.id);
        const field = params.colDef.field;
        return (
          !!rowId &&
          !!field &&
          conflictCellsRef.current.has(cellKey(rowId, field))
        );
      },
    }),
    [],
  );

  return {
    onCellValueChanged,
    cellClassRules,
    isSaving: savingCount > 0,
    conflict,
    applyMine,
    dismissConflict,
    termSheetDialog,
    closeTermSheetDialog,
    feeReminder,
    dismissFeeReminder,
    killRequest,
    confirmKill,
    cancelKill,
  };
}
